<script setup>
import { ref, computed, watch } from "vue";

import loadingIconSrc from "@/assets/images/icons/loading.gif";

const props = defineProps({
  icon: {
    type: String,
    default: "mdi-file-hidden",
  },
  title: {
    type: String,
    default: "No title!",
  },
  details: {
    type: String,
    default: "No details!",
  },
  isStarted: {
    type: Boolean,
    default: false,
  },
  isFinished: {
    type: Boolean,
    default: false,
  },
  isPassed: {
    type: Boolean,
    default: false,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  isSelectable: {
    type: Boolean,
    default: true,
  },
  isExpanded: {
    type: Boolean,
    default: false,
  },
  component: {
    type: Object,
    default: null,
  },
  showDetailsBtnOnlyWhenFailed: {
    type: Boolean,
    default: true,
  },
  showComponentOnlyWhenFailed: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['onSelected', 'onExpanded']);

const isSelected = ref(props.isSelected);
const isExpanded = ref(props.isExpanded);

const isFinished = computed(() => {
  return props.isStarted && props.isFinished;
});

const isOnGoing = computed(() => {
  return props.isStarted && !props.isFinished;
});

function toggleSelect(){
  isSelected.value = !isSelected.value;
}

function toggleExpand() {
  if(isExpanded.value) {
    isExpanded.value = false;
  } 
  else {
    if(isFinished.value) {
      isExpanded.value = true;
    }
  }
}

watch(() => isSelected.value, (newValue) => {
  emit('onSelected', newValue);
});

watch(() => isExpanded.value, (newValue) => {
  emit('onExpanded', newValue);
});

watch(() => props.isSelected, (newValue) => {
  isSelected.value = newValue;
});

watch(() => props.isExpanded, (newValue) => {
  isExpanded.value = newValue;
});

</script>

<template>
  <v-card>
    <v-row>
      <v-col cols="9">
        <v-card-text class="text-h6 text-left">
          <v-icon>{{ props.icon }}</v-icon>
          {{ props.title }}
        </v-card-text>
      </v-col>

      <v-col cols="1">
        <v-card-text class="text-h6 text-center">
          <v-icon @click="toggleExpand()" :class="{ 'cursor-pointer': isFinished }" v-if="(isFinished && !props.isPassed) || (isFinished && !showDetailsBtnOnlyWhenFailed)">mdi-dots-horizontal</v-icon>
        </v-card-text>
      </v-col>

      <v-col cols="1">
        <v-card-text class="text-h6 text-center">
          <v-icon v-if="isFinished" :color="props.isPassed ? 'green' : 'red'" :icon="props.isPassed ? 'mdi-thumb-up' : 'mdi-thumb-down'"></v-icon>
        </v-card-text>
      </v-col>

      <v-col cols="1">
        <v-card-text v-if="isSelectable" class="text-h6 text-center">
          <v-icon v-if="!isOnGoing" @click="toggleSelect" :icon="isSelected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank'"></v-icon>
          <v-img v-if="isOnGoing" class="loading" :src="loadingIconSrc"></v-img>
        </v-card-text>
      </v-col>
    </v-row>

    <v-expand-transition>
      <div v-if="isExpanded" class="mt-2">
        <v-card-text class="text-left">
          <pre>{{ props.details }}</pre>
        </v-card-text>

        <component v-if="(isFinished && !props.isPassed) || (isFinished && !showComponentOnlyWhenFailed)" v-bind:is="props.component"></component>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<style scoped>
.v-card {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 10px;
}

.v-icon {
  margin-right: 10px;
}

.v-col {
  padding: 0px;
}

.loading {
  width: 30px;
}

.cursor-pointer {
  cursor: pointer;
  user-select: none;
}
</style>